// BulkActionBar — selection-aware action strip (CHG-1233 / pack 2).
//
// Renders nothing when `selectedCount === 0` (returns null). When count > 0,
// renders a horizontal strip: "{n} selected" label · action buttons · Clear.
// Sticky positioning is the parent's responsibility — this primitive is the
// strip, not the container.
//
// Per feedback_composition_system.md: pure presentational slot, no internal
// selection state.
//
// CHG-1292 (minor-version bump, 3 of 4 in the audit-approved primitive
// extension sequence): BulkAction gained the optional
// `permissionGate?: { require: string; fallback?: "hide" | "disable" }`
// field. The per-action GatedBulkActionButton component below ONLY calls
// useHasPermission when a gate is declared — so existing ungated callers
// (and their tests) continue to work without a QueryClientProvider
// context. Default fallback is "disable"; "hide" omits.

import type { ReactNode } from "react";
import { useHasPermission } from "../../auth/usePermissions";

export interface BulkActionPermissionGate {
  /** Required permission code (e.g. "alora-giving.donor.delete"). */
  require: string;
  /** Behavior when the user lacks the permission. Defaults to "disable". */
  fallback?: "hide" | "disable";
}

export interface BulkAction {
  id: string;
  label: string;
  icon?: ReactNode;
  variant?: "primary" | "secondary" | "danger";
  disabled?: boolean;
  onClick: () => void;
  /** CHG-1292 — optional RBAC gate. Hook only fires when this is set. */
  permissionGate?: BulkActionPermissionGate;
}

export interface BulkActionBarProps {
  selectedCount: number;
  actions: BulkAction[];
  onClear: () => void;
  /** Override the default "{n} selected" label format. */
  label?: string;
}

const VARIANT_CLASS: Record<NonNullable<BulkAction["variant"]>, string> = {
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
  danger: "btn btn-secondary",
};

const DANGER_STYLE = {
  color: "var(--color-error)",
  borderColor: "var(--color-error)",
} as const;

const DENIED_TOOLTIP = "You don't have permission to perform this action";

// ─────────────────────────────────────────────────────────────────
// Plain bulk-action button — no hook calls. Used when the action
// has no permissionGate. Keeps ungated callers + tests working
// without a QueryClientProvider context.
// ─────────────────────────────────────────────────────────────────

function PlainBulkActionButton({ action }: { action: BulkAction }) {
  return (
    <button
      type="button"
      className={VARIANT_CLASS[action.variant ?? "secondary"]}
      disabled={action.disabled}
      onClick={action.onClick}
      style={action.variant === "danger" ? DANGER_STYLE : undefined}
    >
      {action.icon}
      {action.label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Gated bulk-action button — calls useHasPermission. Only rendered
// when the action has a permissionGate declared.
// ─────────────────────────────────────────────────────────────────

function GatedBulkActionButton({ action }: { action: BulkAction }) {
  const gate = action.permissionGate!;
  const allowed = useHasPermission(gate.require);
  if (allowed) {
    return <PlainBulkActionButton action={action} />;
  }
  const fallback = gate.fallback ?? "disable";
  if (fallback === "hide") {
    return null;
  }
  return (
    <button
      type="button"
      className={VARIANT_CLASS[action.variant ?? "secondary"]}
      title={DENIED_TOOLTIP}
      aria-disabled="true"
      disabled
      style={action.variant === "danger" ? DANGER_STYLE : undefined}
      data-permission-gate-state="denied"
      data-permission-gate-require={gate.require}
    >
      {action.icon}
      {action.label}
    </button>
  );
}

export function BulkActionBar({ selectedCount, actions, onClear, label }: BulkActionBarProps) {
  if (selectedCount <= 0) return null;
  const labelText = label ?? `${selectedCount} selected`;
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 16px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>{labelText}</span>
      <div style={{ display: "flex", gap: 8, flex: 1 }}>
        {actions.map((action) =>
          action.permissionGate ? (
            <GatedBulkActionButton key={action.id} action={action} />
          ) : (
            <PlainBulkActionButton key={action.id} action={action} />
          )
        )}
      </div>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={onClear}
        style={{ fontSize: 12, height: 28, padding: "0 8px" }}
      >
        Clear
      </button>
    </div>
  );
}
